/**
 * Utilitários de impressão para aguardar renderizações/animações do eCharts.
 *
 * Foi constatado no Edge, e possivelmente pode aparecer em outros navegadores, que durante a
 * impressão os gráficos do echarts saiam cortados por serem maiores do que a pagina de impressão.
 *
 * Ao usar a impressão interna (services/print), um seletor de CSS faz com que o eCharts fique menor
 * e caiba na impressão sem cortar.
 *
 * Acontece que essa alteração faz com que o eCharts inicie uma nova renderização, com animação.
 *
 * E necessário aguardar o final da renderização/animação, para poder continuar com a impressão.
 *
 * Esse mecanismo de observação e espera é implementado neste arquivo.
 *
 * A ideia é simples e acontece em alguns passos:
 * - Obtem as instâncias do eCharts que estão renderizadas;
 * - Adiciona rotinas de monitoramento nelas (listeners);
 * - Aguarda 1 segundo para observar se está havendo eventos de renderização (não há um evento
 *  que indique inicio de renderização, mas sim eventos de renderização menores e repetitivos
 *  durante a animação);
 * - Se não houver evento de renderização em 1 segundo, então termina; ou
 * - Se houve alguma renderização, então aguarda o evento "finished", onde todas as renderizações
 *  daquela instância acabaram.
 *
 * Observação: Se um gráfico estiver sofrendo atualizações constantes de dados em intervalos
 * mínimos de tempo, então talvez pode ser necessário diferenciar se a renderização é de dados ou de
 * resize, supondo que o eCharts não dispare o finished entre renderizações continuas de dados
 * diferentes, o que é pouco provável. Porém se isso acontecer, atualmente esse algorítimo não
 * comporta isso, mas também não há previsão desde tipo de processamento até então nos requisitos.
 */
import * as echarts from 'echarts/lib/echarts';

const ECHARTS_RENDER_WAIT_DELAY = 1000;

function getEchartsInstances() {
  return [...document.querySelectorAll('.echarts')]
    .map((el) => {
      const instance = echarts.getInstanceByDom(el);
      if (!instance) {
        throw new Error('Não foi possível recuperar a instância do echarts.');
      }
      return instance;
    });
}

function monEChartsInstance(instance) {
  return new Promise((resolve) => {
    let timeout;
    let isRendering = false;
    function onRendered() {
      if (isRendering) {
        return;
      }

      isRendering = true;
      clearTimeout(timeout);
    }
    function onFinished() {
      isRendering = false;
      instance.off('rendered', onRendered);
      instance.off('finished', onFinished);
      resolve();
    }
    timeout = setTimeout(() => {
      resolve();
    }, ECHARTS_RENDER_WAIT_DELAY);

    instance.on('rendered', onRendered);
    instance.on('finished', onFinished);
  });
}

export default async function waitEcharts() {
  const instances = getEchartsInstances();
  if (instances.length === 0) {
    return;
  }

  const renderings = instances.map(monEChartsInstance);
  await Promise.all(renderings);
}
